import * as React from 'react';
import PropTypes from 'prop-types';
import { twMerge } from 'tailwind-merge';
import { useNavigate } from 'react-router-dom';
import { CheckBox } from '@/components/Elements';
import {
  CaretDown,
  Funnel,
  SortAscending,
  SortDescending,
} from '@phosphor-icons/react';
import { map, uniq } from 'lodash';
import Fade from '@mui/material/Fade';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import clsx from 'clsx';
import {
  FilterDropDownPillTableNew,
  getPostProcessing,
  getTimestamp,
} from './FilterDropDownPillTableNew';
import Tooltip from '@/components/Tooltip';
import useEntityStore from '@/features/entity/store/useEntityStore';
import { useShallow } from 'zustand/react/shallow';
import useDashboardStore from '@/features/dashboard/store/useDashboardStore';
import useExploreStore from '@/features/explore/store/useExploreStore';
import useReportsStore from '@/features/reports/store/useExploreStore';
import useIframe from '@/hooks/useIframe';

const TableOps = ({
  column,
  handleTableOps,
  completeData,
  columnIndex,
  checkedState,
  columns,
  setCheckedState,
  tableOps,
  actualTableRowCount,
  clickedFilter,
  setClickedFilter,
  setActiveColumn,
  activeColumn,
  rowLength,
  from,
  schema,
}) => {
  const [anchorEl, setAnchorEl] = React.useState();
  const [open, setOpen] = React.useState(false);
  const arrowBtnRef = React.useRef();
  const [filterOpen, setFilterOpen] = React.useState(false);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget || event.current);
    setOpen(true);
    setActiveColumn(column);
  };
  React.useEffect(() => {
    if (clickedFilter === column) {
      handleClick(arrowBtnRef);
    }
  }, [column, clickedFilter]);

  const handleClose = () => {
    setOpen(false);
    setClickedFilter();
    setFilterOpen(false);
    if (activeColumn === column) {
      setActiveColumn();
    }
  };

  return (
    <div>
      <button
        className={`opacity-0  group-hover/inner:opacity-100 hover:bg-pv-primary-primary-50 rounded-md p-0.5 ${
          open
            ? 'bg-pv-primary-primary-50 rounded-md p-0.5 opacity-100'
            : 'opacity-0'
        }`}
        onClick={handleClick}
        ref={arrowBtnRef}
      >
        <CaretDown size={15} />
      </button>
      <Popper
        open={open}
        anchorEl={anchorEl}
        transition
        // disablePortal={from === 'dashboard'}
        sx={{ zIndex: 5 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <div
              className="bg-white p-1 rounded-md shadow-lg card-shadow mt-2"
              // onMouseLeave={handleClose}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <div>
                  <button
                    disabled={rowLength <= 1}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTableOps({
                        name: 'sort',
                        field: column,
                        type: 'ascending',
                      });
                      handleClose();
                    }}
                    className={`flex items-center gap-2 px-4 py-2  text-xs ${
                      rowLength <= 1
                        ? 'text-pv-neutral-grey-300'
                        : 'text-brand-ai-200 hover:bg-brand-ai-700 font-normal leading-5 cursor-pointer'
                    } w-full text-left`}
                  >
                    <SortAscending size={15} /> Sort Ascending
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTableOps({
                        name: 'sort',
                        field: column,
                        type: 'descending',
                      });
                      handleClose();
                    }}
                    className={`flex items-center gap-2 px-4 py-2  text-xs ${
                      rowLength <= 1
                        ? 'text-pv-neutral-grey-300'
                        : 'text-brand-ai-200 hover:bg-brand-ai-700 font-normal leading-5 cursor-pointer'
                    } w-full text-left`}
                  >
                    <SortDescending size={15} /> Sort Descending
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    className={`${
                      filterOpen ? 'bg-brand-ai-700' : ''
                    } flex items-center gap-2 px-4 py-2 text-brand-ai-200 text-xs hover:bg-brand-ai-700 font-normal leading-5 cursor-pointer w-full text-left`}
                  >
                    <FilterDropDownPillTableNew
                      title={
                        <div className="gap-2 justify-between flex text-brand-ai-200 items-center text-xs hover:bg-brand-ai-700 font-normal leading-5 cursor-pointer w-full text-left">
                          <Funnel size={15} />
                          Filter
                        </div>
                      }
                      data={uniq(
                        map(completeData, column)
                          .filter((item) => item !== undefined && item !== null)
                          .flat()
                      )}
                      name=""
                      disabled={false}
                      showSearchBox={true}
                      customPositionClass={`absolute ${
                        columnIndex === columns.length - 1
                          ? 'top-5 right-28 mr-0.5'
                          : 'top-5 left-28 mr-0.5'
                      }`}
                      defaultSelectedOptions={checkedState[column]}
                      showApplyCancelResetButton={true}
                      onResetClick={() => {
                        setCheckedState((prev) => {
                          const clonedArr = structuredClone(prev);
                          clonedArr[column] = [];
                          return clonedArr;
                        });
                        handleTableOps({
                          name: 'filter',
                          field: column,
                          type: null,
                        });
                      }}
                      onCancel={() => {
                        handleClose();
                      }}
                      onApply={(data) => {
                        setCheckedState((prev) => {
                          const clonedArr = structuredClone(prev);
                          clonedArr[column] = data;
                          return clonedArr;
                        });
                        handleTableOps({
                          name: 'filter',
                          field: column,
                          type: data.length > 0 ? 'equals' : null,
                          ...(data.length > 0 && { values: data }),
                        });
                        handleClose();
                      }}
                      allowEmpty={false}
                      tableOps={tableOps}
                      columnName={column}
                      actualTableRowCount={actualTableRowCount}
                      clickedFilter={clickedFilter}
                      setFilterOpen={setFilterOpen}
                      schema={schema}
                      from="dashboard"
                    />
                  </button>
                </div>
              </ClickAwayListener>
            </div>
          </Fade>
        )}
      </Popper>
    </div>
  );
};

export const Table = React.forwardRef(
  (
    {
      className,
      rows,
      columns,
      headerClassName,
      columnClass,
      rowClassName,
      onRowClick,
      style,
      checkBoxOnChange,
      checkBoxValue = false,
      rowWrapperClassName,
      from,
      completeData,
      localItems,
      setLocalItems,
      checkedState,
      setCheckedState,
      handleTableOps,
      tableOps,
      currentPageNo,
      actualTableRowCount,
      removeSort,
      msg,
      curItemsPerPage,
      widgetId,
      setMessageDetail,
      totalRowCount,
    },
    ref
  ) => {
    const navigate = useNavigate();
    const handleRowClick = (data, currentPageNo) => {
      if (onRowClick) {
        onRowClick(data, currentPageNo);
      }
    };
    const [lrows, setRows] = React.useState(rows);
    const { isAppInIframe, redirectBasedOnParams } = useIframe();
    // const [sortProperty, setSortProperty] = React.useState();
    // const [sortOrder, setSortOrder] = React.useState();
    React.useEffect(() => {
      setRows(rows);
    }, [rows]);
    const [clickedFilter, setClickedFilter] = React.useState();
    const [activeColumn, setActiveColumn] = React.useState();
    const {
      entityOpen,
      setEntityOpen,
      selectedEntityPetavueIndex,
      selectedColumnName,
      setSelectedEntityPetavueIndex,
      setSelectedColumnName,
      setSelectedColumnLabel,
      setEntityMeta,
      entityMeta,
      setSelectedEntityRowIndex,
      entityRowIndex,
      setFocusId,
      setFocusIdForScroll,
      arrowClicked,
      setArrowClicked,
      setActualWidgets,
    } = useEntityStore(
      useShallow((state) => ({
        entityOpen: state.entityOpen,
        setEntityOpen: state.setEntityOpen,
        selectedEntityPetavueIndex: state.selectedEntityPetavueIndex,
        selectedColumnName: state.selectedColumnName,
        setSelectedColumnLabel: state.setSelectedColumnLabel,
        setSelectedEntityPetavueIndex: state.setSelectedEntityPetavueIndex,
        setSelectedColumnName: state.setSelectedColumnName,
        setEntityMeta: state.setEntityMeta,
        entityMeta: state.entityMeta,
        setSelectedEntityRowIndex: state.setSelectedEntityRowIndex,
        entityRowIndex: state.entityRowIndex,
        setFocusId: state.setFocusId,
        setFocusIdForScroll: state.setFocusIdForScroll,
        arrowClicked: state.arrowClicked,
        setArrowClicked: state.setArrowClicked,
        setActualWidgets: state.setActualWidgets,
      }))
    );
    const { setExploreFocusMode } = useExploreStore(
      useShallow((state) => ({
        setExploreFocusMode: state.setChartsFocusMode,
      }))
    );
    const { setRightPanelState } = useReportsStore(
      useShallow((state) => ({
        setRightPanelState: state.setRightPanelState,
      }))
    );

    const { isDashboardEditable, widgets } = useDashboardStore(
      useShallow((state) => ({
        isDashboardEditable: state.isDashboardEditable,
        widgets: state.widgets,
      }))
    );
    React.useEffect(() => {
      if (
        entityRowIndex !== null &&
        entityRowIndex >= 0 &&
        arrowClicked &&
        msg.displayName === entityMeta?.outputName
      ) {
        setSelectedEntityPetavueIndex(
          localItems?.[entityRowIndex]?.['petavueIndex']
        );
        setEntityMeta({
          ...entityMeta,
          value: localItems?.[entityRowIndex]?.[selectedColumnName],
        });
      }
    }, [entityRowIndex, localItems]);

    return (
      <>
        <div
          className={clsx(
            `w-full bg-white overflow-x-auto`,
            from === 'metricsTable' && lrows.length === 1 && 'h-full',
            from === 'dashboard' && 'h-full scrollbar-hide-y',
            from === 'foh' && ''
          )}
        >
          {from === 'foh' && (
            <div className="absolute bg-white right-3.5 h-5 w-5" />
          )}
          <table
            ref={ref}
            style={style}
            className={twMerge(
              `${from === 'explore' ? 'bg-gray-50' : 'bg-white'} min-w-full`,
              className
            )}
          >
            <thead className={twMerge('bg-gray-50 w-full', headerClassName)}>
              <tr>
                {columns.map((column, columnIndex) => (
                  <th
                    key={columnIndex}
                    scope="col"
                    className={twMerge(
                      `${`group py-3 text-center text-xs text-gray-500 ${
                        (from === 'explore' ||
                          (from === 'dashboard' && widgetId)) &&
                        'bg-pv-primary-primary-100 hover:bg-pv-primary-primary-300'
                      } ${
                        activeColumn === column && 'bg-pv-primary-primary-300'
                      }`}`,
                      columnIndex === 0 && 'rounded-tl-lg',
                      columnIndex === columns.length - 1 && 'rounded-tr-lg',
                      columnClass,
                      from === 'explore' && 'border-l-0',
                      from === 'explore' &&
                        columnIndex === columns.length - 1 &&
                        'border-r-0',
                      from === 'dashboard' &&
                        columnIndex === columns.length - 1 &&
                        'border-l-0',
                      from === 'foh' && 'border-l-0.5'
                    )}
                    // style={{ maxWidth: '400px', textWrap: 'wrap' }}
                  >
                    {column === 'CheckBox' ? (
                      <div key={`div-${checkBoxValue}`}>
                        <CheckBox
                          key={`header-${checkBoxValue}`}
                          className="mx-auto h-3"
                          onChange={checkBoxOnChange}
                          checked={checkBoxValue}
                        />
                      </div>
                    ) : (
                      <div className="flex justify-between gap-2 items-center">
                        {from === 'explore' || from === 'dashboard' ? (
                          <div className="items-center flex gap-2 whitespace-nowrap">
                            {msg?.schema?.[column]?.label
                              ? msg?.schema?.[column].label
                              : column}
                            <div
                              className="flex gap-1"
                              style={{ width: '54px' }}
                            >
                              {tableOps.current.operation
                                .filter((a) => a.field === column)
                                .map((data) =>
                                  data.name === 'filter' ? (
                                    <Tooltip
                                      title="View filter"
                                      placement="top"
                                    >
                                      <button
                                        className="cursor-pointer hover:bg-pv-primary-primary-50 rounded-md p-0.5"
                                        onClick={() => {
                                          setClickedFilter(column);
                                        }}
                                      >
                                        <Funnel size={15} />
                                      </button>
                                    </Tooltip>
                                  ) : data.name === 'sort' &&
                                    data.type === 'ascending' ? (
                                    <Tooltip
                                      title="Remove sort"
                                      placement="top"
                                    >
                                      <button
                                        className="cursor-pointer hover:bg-pv-primary-primary-50 rounded-md p-0.5"
                                        onClick={() => {
                                          removeSort(column);
                                        }}
                                      >
                                        <SortAscending size={15} />
                                      </button>
                                    </Tooltip>
                                  ) : data.name === 'sort' &&
                                    data.type === 'descending' ? (
                                    <Tooltip
                                      title="Remove sort"
                                      placement="top"
                                    >
                                      <button
                                        className="cursor-pointer hover:bg-pv-primary-primary-50 rounded-md p-0.5"
                                        onClick={() => {
                                          removeSort(column);
                                        }}
                                      >
                                        <SortDescending size={15} />
                                      </button>
                                    </Tooltip>
                                  ) : null
                                )}
                            </div>
                          </div>
                        ) : (
                          <div className="font-normal">{column}</div>
                        )}
                        {(from === 'explore' ||
                          (from === 'dashboard' && widgetId)) && (
                          <TableOps
                            column={column}
                            handleTableOps={handleTableOps}
                            completeData={completeData}
                            columnIndex={columnIndex}
                            checkedState={checkedState}
                            columns={columns}
                            setCheckedState={setCheckedState}
                            tableOps={tableOps}
                            actualTableRowCount={actualTableRowCount}
                            clickedFilter={clickedFilter}
                            setClickedFilter={setClickedFilter}
                            setActiveColumn={setActiveColumn}
                            activeColumn={activeColumn}
                            rowLength={lrows?.length}
                            from={from}
                            schema={msg?.schema}
                          />
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {Array.isArray(lrows) &&
                lrows.length > 0 &&
                lrows.map((row, rowIndex) => (
                  <tr
                    className={twMerge(
                      // 'hover:bg-brand-chat-400 cursor-pointer',
                      rowWrapperClassName,
                      `${
                        from === 'metricsTable' &&
                        !row?.enabled &&
                        'bg-pv-neutral-grey-50'
                      }`
                    )}
                    key={rowIndex}
                    onClick={() => handleRowClick(rowIndex, currentPageNo)}
                  >
                    {columns.map((column, columnIndex) => (
                      <td
                        key={columnIndex}
                        className={twMerge(
                          'px-14 py-4 text-center text-sm text-brand-ai-600 whitespace-pre',
                          rowClassName,
                          from !== 'explore' &&
                            from !== 'dashboard' &&
                            rowIndex === lrows.length - 1 &&
                            columnIndex === columns.length - 1 &&
                            'rounded-br-lg',
                          from !== 'explore' &&
                            from !== 'dashboard' &&
                            rowIndex === lrows.length - 1 &&
                            columnIndex === 0 &&
                            'rounded-bl-lg',
                          from === 'explore' &&
                            columnIndex === columns.length - 1 &&
                            'border-r-0',
                          from === 'dashboard' &&
                            columnIndex === columns.length - 1 &&
                            'border-l-0',
                          from === 'explore' && 'border-t-0 border-l-0',
                          from === 'dashboard' && 'border-t-0',
                          from === 'foh' && columnIndex === 0 && 'border-l-0.5',
                          msg?.entityFields?.includes(column) &&
                            'text-pv-primary-primary-500 cursor-pointer hover:bg-pv-primary-primary-50 hover:underline',
                          selectedColumnName === column &&
                            selectedEntityPetavueIndex ===
                              row['petavueIndex'] &&
                            msg?.id === entityMeta.messageId &&
                            msg?.displayName === entityMeta.outputName &&
                            'text-pv-primary-primary-300 cursor-pointer bg-pv-primary-primary-50 underline',
                          isDashboardEditable &&
                            msg?.entityFields?.includes(column) &&
                            'cursor-not-allowed'
                        )}
                        onClick={async () => {
                          if (
                            (from === 'explore' || from === 'dashboard') &&
                            !isDashboardEditable &&
                            Array.isArray(msg?.entityFields) &&
                            msg?.entityFields.includes(column)
                          ) {
                            if (!isAppInIframe) {
                              if (from === 'explore') {
                                setMessageDetail();
                                setExploreFocusMode(true);
                                setRightPanelState('id', null);
                              }
                              !entityOpen && setEntityOpen(true);
                              setArrowClicked(false);
                              setSelectedColumnName(column);
                              setSelectedColumnLabel(
                                msg?.schema?.[column]?.label
                                  ? msg?.schema?.[column].label
                                  : column
                              );
                              setSelectedEntityPetavueIndex(
                                row['petavueIndex']
                              );
                              setSelectedEntityRowIndex(
                                curItemsPerPage * (currentPageNo - 1) + rowIndex
                              );
                              setEntityMeta({
                                conversationId: msg.conversationId,
                                messageId: msg.id,
                                stepId: msg.stepId,
                                outputName: msg.displayName,
                                tableRowCount: totalRowCount,
                                value: getPostProcessing(
                                  row[column],
                                  msg?.schema,
                                  column
                                ),
                              });
                            } else if (
                              typeof redirectBasedOnParams === 'function'
                            ) {
                              try {
                                const source = msg.schema?.[column]?.source;
                                const crmObject =
                                  msg.entityDetails?.[source]?.crmObject;
                                const referenceCol =
                                  msg.entityDetails?.[source]?.referenceCol;
                                redirectBasedOnParams({
                                  objectName: crmObject,
                                  fieldName: column,
                                  value: row?.[column],
                                  gsid: row?.[referenceCol],
                                });
                              } catch (error) {}
                            }
                          }
                          if (
                            from === 'dashboard' &&
                            !isDashboardEditable &&
                            Array.isArray(msg?.entityFields) &&
                            msg?.entityFields.includes(column)
                          ) {
                            setFocusId(widgetId);
                            setFocusIdForScroll(widgetId);
                            setActualWidgets(widgets);
                          }
                        }}
                      >
                        {from === 'explore' || from === 'dashboard'
                          ? React.isValidElement(row[column])
                            ? row[column]
                            : ((typeof row[column] === 'object' &&
                                  msg.schema?.[column].type !== 'JSON' &&
                                  msg.schema?.[column].format !==
                                    'multienum') ||
                                  typeof row[column] === 'boolean') &&
                                row[column] !== null
                              ? JSON.stringify(row[column])
                              : getPostProcessing(
                                  row[column],
                                  msg?.schema,
                                  column
                                )
                          : React.isValidElement(row[column])
                            ? row[column]
                            : (typeof row[column] === 'object' ||
                                  typeof row[column] === 'boolean') &&
                                row[column] !== null
                              ? JSON.stringify(row[column])
                              : row[column]}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {actualTableRowCount > 0 && lrows.length === 0 && (
          <p className="text-pv-text-primary-text mx-auto mt-2 text-center">
            No results available, try refining your filters
          </p>
        )}
      </>
    );
  }
);
Table.displayName = 'Table';

Table.propTypes = {
  className: PropTypes.string,
  rows: PropTypes.array,
  columns: PropTypes.array,
  headerClassName: PropTypes.string,
  rowClassName: PropTypes.string,
};
